/**
 * Experiment resolution.
 *
 * Bucketing, rollout % and the kill switch for every experiment live in the
 * PostHog experiment UI on the corresponding flag. This module only reads the
 * assigned variant. A flag is read through resolveExperimentVariant and nowhere
 * else: posthog-js fires the $feature_flag_called exposure event on that read,
 * which is what enrolls the user in the experiment — so the timeout/blocked
 * path deliberately never reads the flag, and `peekFlag` is the sanctioned
 * NON-enrolling read for UI that must not affect experiment membership.
 */
import { analytics } from 'lib/analytics';

export const EXPERIMENT_FLAG = 'welcome-tour';
export const GET_STARTED_FLAG = 'get-started';
export type ExperimentVariant = 'test' | 'control';
// Back-compat alias for the original single-experiment name.
export type WelcomeTourVariant = ExperimentVariant;

const FLAG_TIMEOUT_MS = 1500;

/**
 * Resolves true once flags have loaded, false when analytics is off or they don't arrive in time. `fresh` waits for
 * a new load instead: right after login, the flags in memory are still the anonymous visitor's.
 */
export function whenFlagsLoaded(timeoutMs: number = FLAG_TIMEOUT_MS, fresh: boolean = false): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    if (!analytics.isReady()) {
      resolve(false);
      return;
    }
    let settled = false;
    const settle = (loaded: boolean) => {
      if (!settled) {
        settled = true;
        resolve(loaded);
      }
    };
    const timer = setTimeout(() => settle(false), timeoutMs);
    // onFeatureFlags replays the flags in memory at once; a fresh wait skips that replay.
    let armed = !fresh;
    try {
      analytics.onFeatureFlags(() => {
        if (!armed) return;
        clearTimeout(timer);
        settle(true);
      });
      if (fresh) {
        armed = true;
        analytics.reloadFeatureFlags();
      }
    } catch {
      clearTimeout(timer);
      settle(false);
    }
  });
}

export function resolveExperimentVariant(flagKey: string, timeoutMs: number = FLAG_TIMEOUT_MS): Promise<ExperimentVariant> {
  return whenFlagsLoaded(timeoutMs).then((loaded) => {
    // A timeout already sent this user to the control experience, so the flag
    // must never be READ then: getFeatureFlag fires the exposure event, which
    // would enrol someone who was never shown the variant and quietly poison
    // the experiment's results.
    if (!loaded) return 'control';
    try {
      return analytics.getFeatureFlag(flagKey) === 'test' ? 'test' : 'control';
    } catch {
      return 'control';
    }
  });
}

export const resolveWelcomeTourVariant = (timeoutMs: number = FLAG_TIMEOUT_MS): Promise<ExperimentVariant> =>
  resolveExperimentVariant(EXPERIMENT_FLAG, timeoutMs);

export const resolveGetStartedVariant = (timeoutMs: number = FLAG_TIMEOUT_MS): Promise<ExperimentVariant> =>
  resolveExperimentVariant(GET_STARTED_FLAG, timeoutMs);

/** Read a flag's cached value WITHOUT firing $feature_flag_called (no enrollment). */
export function peekFlag(flagKey: string): string | boolean | undefined {
  if (!analytics.isReady()) return undefined;
  try {
    return analytics.getFeatureFlag(flagKey, false);
  } catch {
    return undefined;
  }
}
